// Terms and Conditions text, copied word for word from the app (LegalModal.tsx on
// redesign/legal-20261019) via redesign-one-brain-plan/legal-20261019/website/terms.txt.
// Keep it identical to the app: replace the whole string when the app text changes.
// Rendered by components/legal/PlainTextDocument.jsx.
const text = `Terms and Conditions – Thryvin'

Last updated: 19 October 2026
Effective date: 19 October 2026

These Terms are the agreement between you and Jake West, trading as Thryvin' ("Thryvin'", "we" or "us"), for using the Thryvin' app (the "App"). By creating an account or using the App, you agree to these Terms. If you join Community, you also agree to the Community Guidelines. Our Privacy Policy explains how we use your information.

If you don't agree, please don't use the App.


THE SHORT VERSION

• Thryvin' gives advice, not instructions. You decide what's right for you.
• It isn't medical advice. AI can get things wrong.
• Always check food labels for allergens. Never rely on the App for a serious allergy.
• See your GP (or midwife if you're pregnant) before starting, if you have any health worries.
• Stop if something hurts or feels wrong. In an emergency, call 999.


1. WHAT THRYVIN' IS

Thryvin' gives you a personal training plan, workout logging, food and nutrition tracking, an AI coach and an optional Community. It offers general fitness and food guidance to help you stay consistent.

Everything Thryvin' tells you is advice for you to consider, not instructions you must follow. You decide what's right for your body and your food, and you can always ask a qualified professional.

Thryvin' is not a medical, dietetic, midwifery, physiotherapy or rehabilitation service. It doesn't diagnose, treat or prevent any condition. It is not an emergency service: in an emergency call 999, and for urgent medical help that isn't an emergency call NHS 111.


2. WHO CAN USE THRYVIN'

• You must be 18 or over.
• Give us accurate information and keep your login safe. You're responsible for what happens on your account. If you think someone else is using it, tell us at support@thryvin.app.
• One person per account.


3. HEALTH, FITNESS AND FOOD: PLEASE READ

3.1 Not medical or dietetic advice
• Everything in the App, including plans, food targets, meal plans, recipes and coach messages, is general information. It is not medical or dietetic advice.
• Talk to a GP or registered dietitian before you start if you have a medical condition, take medicine that affects eating or exercise, or have (or have had) an eating disorder. If you're pregnant or have had a baby in the last year, talk to your midwife or GP (see 3.7).
• Stop exercising straight away and get help if you have chest pain or tightness, bad breathlessness, feel faint or dizzy, have a racing or uneven heartbeat, sudden bad pain, or anything that worries you. Call 999 in an emergency, or NHS 111 if you're not sure.
• If tracking calories or food is making you anxious, stop tracking and talk to someone you trust or your GP. Beat (beateatingdisorders.org.uk) helps anyone worried about eating.
• Thryvin' is made for healthy adults. It isn't designed to manage diabetes, kidney, heart or other conditions where food or exercise needs medical supervision.

3.2 Numbers are estimates
• Food targets, recipe nutrition, photo and describe-it results, food database values and calories burned are estimates. They can be wrong.
• Calorie and macro targets are worked out from the details you give us, with safe minimums built in. They're a starting point, not a prescription. Don't go below them without medical advice.
• Check and edit an entry before you log it.

3.3 Allergies: always check the pack
• We try to keep foods you avoid out of your meals and to warn you about them. But we can't promise that any food, recipe, meal plan, shopping list, scan result or product is free from an allergen, even if you've told us about your allergies.
• Our information comes from food databases, product data added by volunteers, and AI. Any of these can be wrong or out of date. Makers change their recipes, and "may contain" and cross-contamination risks aren't always listed.
• Check the pack to be safe, every time, especially if you have a serious allergy. Never rely on the App alone.
• If you have a serious allergy (for example one that has caused anaphylaxis), use Thryvin' only for ideas. Read every label yourself, ask when eating out, and follow the advice of your GP or allergy clinic.
• Recipes and workouts shared by other members in Community are not checked by us for allergens, safety or accuracy.

3.4 Scanner scores
• Scores and ratings from the barcode scanner are information only. They aren't a safety or health rating. They're partly based on Open Food Facts data, which is added by volunteers and can be wrong.

3.5 Meal prep and food safety
Our meal-prep keep times follow UK Food Standards Agency advice:
• Keep your fridge at 5°C or below.
• Eat cooked leftovers from the fridge within 2 days, or freeze them. In Thryvin, day 3 onwards comes from the freezer.
• Eat cooked rice within 24 hours, and keep it in the fridge.
• Eat food you've defrosted within 24 hours.
• Reheat food only once, until it's steaming hot all the way through.

You're responsible for buying, storing, cooking and reheating food safely. Our keep times and storage tips are general guidance based on FSA advice. They don't guarantee food is safe. Always follow the use-by date and the instructions on the pack, and throw food away if you're not sure. If you're pregnant, older, or have a weakened immune system, stricter NHS food safety advice applies.

3.6 Injuries
• If you tell us about injuries, Thryvin tries to avoid exercises that could make them worse. This doesn't guarantee you won't get hurt. Listen to your body and stop if something hurts.
• Thryvin' doesn't diagnose injuries or replace a GP or physiotherapist. Get pain checked if it's new, bad or not getting better.

3.7 Pregnancy and after the birth
• Telling us you're pregnant or have had a baby is optional. If you do, Thryvin' tries to keep your training, meals and coach tips in line with general NHS guidance for your stage.
• This is general guidance, not antenatal or postnatal care. Check with your midwife or GP before starting or changing exercise, and follow their advice over ours.
• Stop and call your maternity unit or midwife straight away if you have bleeding, fluid leaking, regular tightenings or pain, your baby moving less, dizziness, headaches or blurred vision, or anything that worries you. Call 999 in an emergency.
• After the birth, wait for your 6 to 8 week check before doing more than walking and pelvic floor exercises, unless your GP or midwife says otherwise.

3.8 Health readings and devices
• Readings from Apple Health, Health Connect, Strava or a wearable can be missing or wrong. Thryvin' only uses them as a guide.


4. AI-MADE CONTENT

• Some content is made by AI, including coach replies, plans, meal plans, recipe and fridge ideas, and food estimates from photos or descriptions.
• AI can be wrong, incomplete or unsuitable for you, even with our safety rules. No person checks each reply before you see it. Treat it as a suggestion: check ingredients against your allergies, and check that a workout suits you before you do it.
• If the coach mentions an emergency number or a health service, contact them. Don't wait for the App.
• AI only runs if you give permission (see the Privacy Policy). Some features don't work without it.


5. PRO, SUBSCRIPTIONS AND PLAN CREDITS

5.1 Subscriptions
• Thryvin' has a free version and a paid Pro subscription. What each one includes is shown in the App and can change over time.
• Payments are taken by Apple App Store or Google Play. You'll see the price, billing period, any free trial and the renewal terms before you buy.
• Subscriptions renew automatically until you cancel. Manage or cancel them in your Apple App Store or Google Play account. Deleting your Thryvin account doesn't cancel the subscription.
• Refunds are handled by Apple App Store or Google Play under its rules. This doesn't affect your legal rights.
• Pro is digital content that starts as soon as you buy it. By buying, you ask for it to start straight away, so the usual 14-day right to cancel ends once it starts. Apple App Store or Google Play's own refund rules still apply.

5.2 Plan credits (Pro)
• Pro includes 50 plan credits a month, shared by your training and your meals. Credits are only used when the App's AI builds something new for you: a full rebuild of your training plan or meal plan uses 10, the AI rebuilding several days at once uses 2, and a new workout from your coach or one day rebuilt by the AI uses 1. The App always shows the cost and what you have left before you confirm.
• Everyday changes don't use credits, whether you or your coach make them: moving, swapping or skipping a session, rest days, swapping exercises, changing sets or reps, copying a Community workout, adding a recipe to your meal plan, swapping or skipping a meal, and portions. Chatting with your coach, logging, the plan your monthly check-in builds, and changes made for your safety (such as working around an injury) are free too.
• If you don't have enough credits, nothing is charged and the App tells you when they reset. Coach use also has a fair-use limit.
• Credits reset on the 1st of each month in your time zone. Unused credits don't carry over.
• Credits have no cash value, can't be bought, sold or transferred, and end when Pro ends.
• We may change the number of credits or what things cost. We'll tell you in the App first.

5.3 Fair use
• "Unlimited" coach chat has a hidden daily safety limit to stop automated misuse. Normal use won't reach it.


6. UPDATES

• We update the App to fix problems, add features and keep it secure and legal.
• Sometimes an update is required, for example when our privacy wording changes or an old version can't work with our servers. When that happens, the App asks you to update and can't be used until you do. Your data is kept.
• We may change, pause or stop features. If we stop something you're paying for, we'll tell you first.


7. COMMUNITY

7.1 Joining
• Community is optional and for adults (18+) only. You accept the Community Guidelines when you join.
• Your nickname must be 3 to 20 letters, numbers or underscores. It can't contain rude or explicit words, and can't pretend to be Thryvin or someone else. You can change it once every 30 days.
• Your profile photo is optional and must be of you. It gets the same automatic safety check as other photos, and only your friends see it.

7.2 What's not allowed
• Hate, harassment, bullying or threats
• Sexual content or nudity
• Any sexual content involving children. We have zero tolerance: we ban the account and report it to the authorities.
• Promoting self-harm or eating disorders
• Selling or promoting steroids, drugs or other dangerous substances
• Extreme diet advice
• Spam, adverts and scams
• Pretending to be someone else
• Sharing other people's private information
• Photos of other people in reactions, and sharing other people's reactions or photos outside the App
• Anything illegal

7.3 Moderation and enforcement
• Photos and posts are checked automatically for safety before or when others see them, and members can report anything. Automatic checks can miss things.
• We can hide or remove content, give warnings, suspend Community access for 7 days, ban someone from Community, or close an account.
• Two removals in 30 days means a 7-day suspension. A third means a Community ban. Child sexual abuse material or threats mean the account is banned at once.
• If we remove your content, we'll tell you and say why.

7.4 Appeals and complaints
• If you think we got it wrong, reply to the notice or email support@thryvin.app within 14 days. We aim to answer within 7 days.
• To report something, use Report in the App or email support@thryvin.app. We aim to review reports about self-harm, sexual content, under-18s, threats or photos within 24 hours, and everything else within 72 hours.

7.5 Your content
• You own what you post. You give Thryvin' permission (a worldwide, non-exclusive, royalty-free licence) to store, show and share it inside the App, including in share cards you choose to make, for as long as it's on Thryvin'. This ends when you delete it, apart from copies kept for safety and legal reasons (see the Privacy Policy).
• When you share a workout or recipe, the members you shared it with may save, copy and use it for themselves, even after you delete the original.
• Only post content you have the right to share.


8. USING THE APP FAIRLY

You agree not to:
• Use the App for anything illegal
• Misuse, overload, scrape, reverse engineer or try to break the App or its systems
• Get around limits, paywalls or safety checks
• Create accounts automatically or use someone else's account
• Give false or misleading information

We may limit or close your access if these Terms are broken.


9. OUR CONTENT

The App, its design, text, graphics, exercise videos, recipes and software belong to us or are licensed to us. You may use them for your own personal use only. Food data comes from the sources listed in our Privacy Policy, under their own licences.


10. ENDING THINGS

• You can stop using Thryvin' and delete your account at any time in You → Settings → Delete account.
• We may suspend or close your account if you break these Terms, if the law requires it, or to protect other people or the App. Where it's reasonable, we'll tell you why and give you a chance to respond.


11. WHAT WE'RE RESPONSIBLE FOR

• We don't promise any fitness, weight or health result. Results are different for everyone.
• We work hard to keep the App working and accurate, but we can't promise it will always be available or free of mistakes.
• If we break these Terms, or don't use reasonable care and skill, we're responsible for loss or damage you suffer that is a foreseeable result of that. Loss or damage is foreseeable if it was obvious it would happen, or if we both knew it might happen when you started using the App.
• We aren't responsible for loss or damage that wasn't foreseeable, for business losses (Thryvin' is for personal use), or for loss or damage caused by you not following these Terms or the safety guidance in Section 3. That includes relying on the App for allergen information instead of checking the label.
• Content shared by other members is theirs. We aren't responsible for it, but we act on reports (Section 7).
• If digital content we supply damages your device or other digital content because we didn't use reasonable care and skill, we'll repair the damage or pay you fair compensation.
• Apart from the things in the next point, the most we'll be responsible for, in total, under these Terms is the greater of £100 or the amount you paid for Pro in the 12 months before the claim.
• Nothing in these Terms limits or excludes our liability for death or personal injury caused by our negligence, for fraud or fraudulent misrepresentation, or for anything else the law doesn't allow us to limit. This includes your rights under the Consumer Rights Act 2015.


12. CHANGES TO THESE TERMS

We may update these Terms. We'll change the "Last updated" date and, before significant changes apply, tell you in the App or by email. If you don't agree with a change, you can stop using the App and delete your account.


13. OTHER LEGAL TERMS

• These Terms, the Privacy Policy and (if you join) the Community Guidelines are the whole agreement between you and us about the App.
• We keep a record of which version of these Terms you agreed to and when. When we make a significant change, the App asks you to read and agree again.
• If a court decides part of these Terms can't be enforced, the rest still applies.
• If we don't enforce a term straight away, we can still enforce it later.
• We may transfer this agreement to someone who takes over Thryvin'. We'll tell you, and it won't reduce your rights. You can't transfer your account.
• Nobody else has rights under these Terms, except Apple and its subsidiaries if you got the App from the Apple App Store (see below).

If you got the App from the Apple App Store: these Terms are between you and us, not Apple. Apple isn't responsible for the App, its content, maintenance or support, or for any claims about it (including product liability, legal compliance or intellectual property claims). If the App doesn't meet a warranty that applies, you can tell Apple, and it may refund what you paid for the App. Apple has no other warranty duty. You must also follow Apple's App Store terms.


14. LAW AND DISPUTES

These Terms are governed by the law of England and Wales. If you live in Scotland or Northern Ireland, you can also bring a claim in your local courts. If you live elsewhere in the UK or the EU, you keep any protection your local law gives you.

Please contact us first so we can try to sort things out.


15. CONTACT

Questions about these Terms:
Email: support@thryvin.app
Post: Jake West (Thryvin'), 11 Netley Close, BH15 3NW, United Kingdom
`;

export default text;
